import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/Layout";
import { api, API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { Cable, Copy, Eye, EyeOff, RefreshCw, CheckCircle2, Mail, Upload as UploadIcon } from "lucide-react";
import { toast } from "sonner";

export default function LabLIS() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [showSecret, setShowSecret] = useState(false);

  const load = () => user?.lab_id && api.get(`/labs/${user.lab_id}/lis`).then((r) => setCfg(r.data)).catch(() => {});
  useEffect(() => { if (user?.lab_id) load(); }, [user?.lab_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const rotate = async () => {
    if (!user?.lab_id) return;
    try {
      const r = await api.post(`/labs/${user.lab_id}/lis/rotate`);
      setCfg((c) => ({ ...c, secret: r.data.secret }));
      toast.success("Secret rotated");
    } catch { toast.error("Rotation failed"); }
  };
  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };

  if (!user?.lab_id) return <DashboardLayout role="lab_admin"><Card className="p-8">No lab attached.</Card></DashboardLayout>;

  const webhookAbs = cfg?.webhook_url?.startsWith("http") ? cfg.webhook_url : `${API_BASE.replace(/\/api$/, "")}${cfg?.webhook_url || ""}`;

  return (
    <DashboardLayout role="lab_admin">
      <h1 className="font-heading font-bold text-3xl text-ink flex items-center gap-2"><Cable className="w-6 h-6 text-efgh-green" /> LIS Connection</h1>
      <p className="text-ink/60">Connect your Laboratory Information System so reports flow into EFGH automatically.</p>

      {cfg?.connected && (
        <Card className="mt-6 p-4 rounded-2xl bg-efgh-green/10 border-efgh-green/30 flex items-center gap-3" data-testid="lis-connected">
          <CheckCircle2 className="w-5 h-5 text-efgh-green" />
          <div className="text-sm text-ink/85">Connected via Webhook · Last sync: {cfg.last_sync ? new Date(cfg.last_sync).toLocaleString() : "—"}</div>
        </Card>
      )}

      {/* Methods */}
      <div className="mt-6 grid md:grid-cols-3 gap-4">
        <Method icon="🔗" title="API / Webhook" tag="Active" tagBg="bg-efgh-green/15 text-efgh-green"
          body="Your LIS pushes report PDFs directly to an EFGH endpoint the moment they're generated. Fastest method — reports appear within seconds." />
        <Method icon="📧" title="Email-to-Report" tag="Available" tagBg="bg-muted text-ink/60"
          body="Your LIS or staff emails (or CCs) the report PDF to a dedicated EFGH address. We parse the attachment automatically." />
        <Method icon="📤" title="Manual Upload" tag="Always on" tagBg="bg-muted text-ink/60"
          body="Your front-desk uploads PDFs one at a time or in batches. Works from day one, no integration needed." />
      </div>

      {/* Credentials */}
      <Card className="mt-6 p-6 rounded-2xl">
        <div className="font-heading font-semibold text-ink text-lg">API credentials</div>
        <div className="mt-4 space-y-4">
          <Row label="Webhook endpoint URL" desc="Give this to your LIS vendor (e.g. CrelioHealth support) to configure outbound report delivery.">
            <code className="text-sm text-ink/85 flex-1 truncate" data-testid="lis-webhook">{webhookAbs}</code>
            <button onClick={() => copy(webhookAbs)} className="text-ink/60 hover:text-ink"><Copy className="w-4 h-4" /></button>
          </Row>
          <Row label="API secret key" desc="Used to authenticate incoming requests via X-EFGH-Signature header (HMAC-SHA256 of file bytes).">
            <code className="text-sm text-ink/85 flex-1 truncate" data-testid="lis-secret">{showSecret ? cfg?.secret : "sk_lis_" + "•".repeat(20)}</code>
            <button onClick={() => setShowSecret((s) => !s)} className="text-ink/60 hover:text-ink">{showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
            <button onClick={() => copy(cfg?.secret || "")} className="text-ink/60 hover:text-ink"><Copy className="w-4 h-4" /></button>
          </Row>
          <Row label="Dedicated email-to-report address" desc="Forward or CC report PDFs to this address from your LIS.">
            <code className="text-sm text-ink/85 flex-1 truncate"><Mail className="w-3.5 h-3.5 inline mr-1 text-efgh-blue" />{cfg?.email_intake}</code>
            <button onClick={() => copy(cfg?.email_intake || "")} className="text-ink/60 hover:text-ink"><Copy className="w-4 h-4" /></button>
          </Row>
        </div>

        <div className="mt-6 pt-6 border-t border-border flex items-center justify-between gap-3">
          <div>
            <div className="font-heading font-semibold text-ink text-sm">Regenerate secret key</div>
            <div className="text-xs text-ink/60">Invalidates the current key immediately. You&apos;ll need to update it with your LIS vendor.</div>
          </div>
          <Button variant="outline" onClick={rotate} data-testid="rotate-secret"><RefreshCw className="w-4 h-4 mr-2" />Regenerate key</Button>
        </div>
      </Card>

      <Card className="mt-6 p-6 rounded-2xl">
        <div className="font-heading font-semibold text-ink text-lg">Sample cURL request</div>
        <pre className="mt-3 p-4 bg-ink text-white text-xs rounded-xl overflow-auto"><code>{`SIG="sha256=$(openssl dgst -sha256 -hmac "$SECRET" -binary report.pdf | xxd -p -c 256)"
curl -X POST "${webhookAbs}" \\
  -H "X-EFGH-Signature: $SIG" \\
  -F "file=@report.pdf;type=application/pdf" \\
  -F "patient_name=Anjali Sharma" \\
  -F "patient_phone=9876543210" \\
  -F "patient_email=anjali.s@email.com" \\
  -F "patient_whatsapp=9876543210" \\
  -F "package_name=Comprehensive Health"`}</code></pre>
        <p className="mt-3 text-xs text-ink/60">
          <b>Contact fields are optional</b> — if omitted, EFGH auto-extracts <code className="font-mono">patient_phone</code>, <code className="font-mono">patient_email</code> and <code className="font-mono">patient_whatsapp</code> from the report header via OCR. Values you send always take priority.
        </p>
      </Card>
    </DashboardLayout>
  );
}

function Method({ icon, title, tag, tagBg, body }) {
  return (
    <Card className="p-5 rounded-2xl">
      <div className="flex items-start justify-between">
        <div className="text-3xl">{icon}</div>
        <span className={`text-[10px] font-semibold px-2 py-1 rounded-full ${tagBg}`}>{tag}</span>
      </div>
      <div className="mt-3 font-heading font-semibold text-ink">{title}</div>
      <p className="mt-1 text-sm text-ink/60">{body}</p>
    </Card>
  );
}

function Row({ label, desc, children }) {
  return (
    <div>
      <div className="text-sm font-medium text-ink">{label}</div>
      <div className="text-xs text-ink/50 mb-2">{desc}</div>
      <div className="flex items-center gap-2 p-3 rounded-lg border border-border bg-muted/30">
        {children}
      </div>
    </div>
  );
}
